import { motion } from 'motion/react';
import type { MarketStoryline } from '@/mocks/marketStorylinesMock';

type Props = {
  storylines: MarketStoryline[];
  onStorylineClick?: (storyline: MarketStoryline) => void;
  className?: string;
  maxVisible?: number;
};

function directionBadgeClass(direction: string | undefined): string {
  switch (direction?.toLowerCase()) {
    case 'bullish':
      return 'bg-emerald-50 text-emerald-800';
    case 'bearish':
      return 'bg-rose-50 text-rose-800';
    case 'mixed':
      return 'bg-violet-50 text-violet-800';
    default:
      return 'bg-amber-50 text-amber-800';
  }
}

export function StorylineCards({
  storylines,
  onStorylineClick,
  className = '',
  maxVisible,
}: Props) {
  const visible = maxVisible != null ? storylines.slice(0, maxVisible) : storylines;
  if (visible.length === 0) return null;

  return (
    <div
      className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 ${className}`}
    >
      {visible.map((storyline, index) => {
        const CardWrapper = onStorylineClick ? 'button' : 'div';
        return (
          <motion.div
            key={storyline.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
          >
            <CardWrapper
              type={onStorylineClick ? 'button' : undefined}
              onClick={onStorylineClick ? () => onStorylineClick(storyline) : undefined}
              className={`flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 text-left transition-all duration-300 ${
                onStorylineClick ? 'cursor-pointer hover:border-gray-900 group' : ''
              }`}
            >
              <div className="mb-3 flex items-center gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-indigo-600 text-xs font-medium text-white">
                  {storyline.rank}
                </span>
                <span
                  className={`rounded-lg px-2 py-0.5 text-xs capitalize ${directionBadgeClass(storyline.direction)}`}
                >
                  {storyline.direction}
                </span>
              </div>

              <h4 className="mb-2 text-sm font-medium leading-snug text-gray-900 transition-transform group-hover:translate-x-0.5">
                {storyline.title}
              </h4>

              <p className="mb-4 flex-1 text-xs leading-relaxed text-gray-600 line-clamp-4">
                {storyline.summary}
              </p>

              {storyline.tickers?.length ? (
                <div className="mb-3 flex flex-wrap gap-1.5">
                  {storyline.tickers.slice(0, 3).map((ticker) => (
                    <span
                      key={ticker}
                      className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600"
                    >
                      {ticker}
                    </span>
                  ))}
                </div>
              ) : null}

              {storyline.tags?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {storyline.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-gray-50 px-2 py-0.5 text-[10px] text-gray-500"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </CardWrapper>
          </motion.div>
        );
      })}
    </div>
  );
}
